// Pure helpers that project a TripReport into time-ordered views:
//  - a flat timeline of every event (banner / step / reroute / tts / discrepancy)
//  - the GPS trace (all records that carry lat/lng, ordered by ts)
//  - minute buckets for the play-through scrubber

import type {
  Discrepancy,
  GuardEvaluation,
  Reroute,
  StepAdvance,
  TimelineEvent,
  TripReport,
  TtsEvent,
} from "./types.js";

export interface TracePoint {
  ts: number;
  lat: number;
  lng: number;
}

export function buildTrace(report: TripReport): TracePoint[] {
  const pts: TracePoint[] = [];
  const push = (r: { ts: number; lat: number | null; lng: number | null }) => {
    if (r.lat != null && r.lng != null) pts.push({ ts: r.ts, lat: r.lat, lng: r.lng });
  };
  report.records.guardEvaluations.forEach(push);
  report.records.stepAdvances.forEach(push);
  report.records.reroutes.forEach(push);
  return pts.sort((a, b) => a.ts - b.ts);
}

export function buildTimeline(report: TripReport): TimelineEvent[] {
  const events: TimelineEvent[] = [];

  report.records.guardEvaluations.forEach((g: GuardEvaluation) =>
    events.push({
      kind: "guard",
      ts: g.ts,
      lat: g.lat,
      lng: g.lng,
      label: `guard ${g.guard} -> ${g.decision}`,
      source: g,
    }),
  );
  report.records.stepAdvances.forEach((s: StepAdvance) =>
    events.push({
      kind: "step",
      ts: s.ts,
      lat: s.lat,
      lng: s.lng,
      label: `advance step ${s.fromStep} -> ${s.toStep} (${Math.round(s.distanceToManeuverM)} m)`,
      source: s,
    }),
  );
  report.records.reroutes.forEach((r: Reroute) =>
    events.push({
      kind: "reroute",
      ts: r.ts,
      lat: r.lat,
      lng: r.lng,
      label: `reroute: ${r.reason} (off ${Math.round(r.offRouteM)} m)`,
      source: r,
    }),
  );
  report.records.ttsEvents.forEach((t: TtsEvent) =>
    events.push({
      kind: "tts",
      ts: t.ts,
      lat: null,
      lng: null,
      label: `tts ${t.phase}: "${t.text}"`,
      source: t,
    }),
  );
  report.discrepancies.forEach((d: Discrepancy) =>
    events.push({
      kind: "discrepancy",
      ts: d.ts,
      lat: d.lat,
      lng: d.lng,
      label: `discrepancy: ${d.detector}`,
      severity: d.severity,
      source: d,
    }),
  );

  return events.sort((a, b) => a.ts - b.ts);
}

export interface TimeBounds {
  start: number;
  end: number;
  minuteCount: number;
}

export function timeBounds(events: TimelineEvent[]): TimeBounds {
  if (events.length === 0) return { start: 0, end: 0, minuteCount: 0 };
  const start = events[0].ts;
  const end = events[events.length - 1].ts;
  const minuteCount = Math.max(1, Math.ceil((end - start) / 60_000));
  return { start, end, minuteCount };
}

/** Minute index (0-based) of a timestamp relative to the trip start. */
export function minuteOf(ts: number, bounds: TimeBounds): number {
  if (bounds.minuteCount === 0) return 0;
  return Math.floor((ts - bounds.start) / 60_000);
}
